import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCattle } from '@/contexts/CattleContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { FileText, Upload, Check } from 'lucide-react';

export default function CattleRegistry() {
  const { addCattle } = useCattle();
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    tag: '',
    name: '',
    breed: '',
    birthDate: '',
    gender: '',
    location: '',
    status: 'Activo',
    hasRegistry: false,
    registryNumber: '',
    purchaseDate: '',
    purchaseWeight: '',
    purchaseFarm: '',
    notes: '',
    imageUrl: ''
  });

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
      toast.success('Imagen cargada correctamente');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.tag || !formData.name || !formData.breed) {
      toast.error('Por favor complete los campos obligatorios');
      return;
    }

    addCattle(formData);
    toast.success(`Ganado ${formData.name} registrado exitosamente`, {
      description: `Tag: ${formData.tag} - ${formData.breed}`
    });
    
    // Reset form
    setFormData({
      tag: '',
      name: '',
      breed: '',
      birthDate: '',
      gender: '',
      location: '',
      status: 'Activo',
      hasRegistry: false,
      registryNumber: '',
      purchaseDate: '',
      purchaseWeight: '',
      purchaseFarm: '',
      notes: '',
      imageUrl: ''
    });
    setImagePreview(null);
    
    setTimeout(() => navigate('/ganaderia'), 1500);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center shadow-md">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Registro de Ganado</h1>
            <p className="text-muted-foreground">Agregue nuevo ganado al sistema</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card className="lg:col-span-2 border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle>Información del Ganado</CardTitle>
            <CardDescription>Complete los datos del animal a registrar</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tag">Tag / Arete *</Label>
                  <Input
                    id="tag"
                    placeholder="G-001"
                    value={formData.tag}
                    onChange={(e) => handleChange('tag', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Toro Alpha"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="breed">Raza *</Label>
                  <Select value={formData.breed} onValueChange={(value) => handleChange('breed', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione raza" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Angus">Angus</SelectItem>
                      <SelectItem value="Hereford">Hereford</SelectItem>
                      <SelectItem value="Brahman">Brahman</SelectItem>
                      <SelectItem value="Charolais">Charolais</SelectItem>
                      <SelectItem value="Simmental">Simmental</SelectItem>
                      <SelectItem value="Criollo">Criollo</SelectItem>
                      <SelectItem value="Mestizo">Mestizo</SelectItem>
                      <SelectItem value="Otra">Otra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Género</Label>
                  <Select value={formData.gender} onValueChange={(value) => handleChange('gender', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione género" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Macho">Macho</SelectItem>
                      <SelectItem value="Hembra">Hembra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birthDate">Fecha de Nacimiento</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => handleChange('birthDate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Ubicación</Label>
                  <Input
                    id="location"
                    placeholder="Ej: Corral A"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                  />
                </div>
              </div>

              {/* Registry Info */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Información de Registro</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="hasRegistry"
                      checked={formData.hasRegistry}
                      onChange={(e) => {
                        handleChange('hasRegistry', e.target.checked);
                        if (!e.target.checked) {
                          handleChange('registryNumber', '');
                        }
                      }}
                      className="w-4 h-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                    />
                    <Label htmlFor="hasRegistry" className="font-medium cursor-pointer">
                      Este animal tiene registro genealógico
                    </Label>
                  </div>
                  
                  {formData.hasRegistry && (
                    <div className="space-y-2 ml-6">
                      <Label htmlFor="registryNumber">Número de Registro</Label>
                      <Input
                        id="registryNumber"
                        placeholder="Ej: ANG-2024-001"
                        value={formData.registryNumber}
                        onChange={(e) => handleChange('registryNumber', e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Este número identifica al animal en el registro genealógico oficial
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Info */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Información de Compra</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="purchaseDate">Fecha de Compra</Label>
                    <Input
                      id="purchaseDate"
                      type="date"
                      value={formData.purchaseDate}
                      onChange={(e) => handleChange('purchaseDate', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchaseWeight">Peso Inicial (kg)</Label>
                    <Input
                      id="purchaseWeight"
                      type="number"
                      placeholder="280"
                      value={formData.purchaseWeight}
                      onChange={(e) => handleChange('purchaseWeight', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchaseFarm">Ganadería de Compra</Label>
                    <Input
                      id="purchaseFarm"
                      placeholder="Ej: Ganadería San José"
                      value={formData.purchaseFarm}
                      onChange={(e) => handleChange('purchaseFarm', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Nombre de la ganadería donde se compró el animal
                    </p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notas / Observaciones</Label>
                <Textarea
                  id="notes"
                  placeholder="Información adicional sobre el ganado..."
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                />
              </div>

              <Button type="submit" size="lg" className="w-full shadow-lg hover:shadow-xl transition-all">
                <Check className="mr-2 h-5 w-5" />
                Registrar Ganado
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Image Upload */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle>Imagen del Ganado</CardTitle>
            <CardDescription>Suba una foto del animal</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-rose-400 transition-colors">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  {imagePreview ? (
                    <div className="space-y-2">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg mb-2"
                      />
                      <p className="text-sm text-muted-foreground">Click para cambiar imagen</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                      <p className="text-sm font-medium">Click para subir imagen</p>
                      <p className="text-xs text-muted-foreground">PNG, JPG hasta 10MB</p>
                    </div>
                  )}
                </label>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-rose-600" />
                  Consejos para la foto
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                  <li>Tome la foto con buena iluminación</li>
                  <li>Capture el perfil completo del animal</li>
                  <li>Evite sombras y reflejos</li>
                  <li>Mantenga el ganado en posición natural</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
